// OrderItemsTable.jsx
import React, { useMemo, useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Divider,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";

function Num({ v }) {
    if (v === null || v === undefined || v === "") return "";
    const n = Number(v);
    if (Number.isNaN(n)) return v;
    return n.toLocaleString("en-IN", { maximumFractionDigits: 3 });
}

export default function OrderItemsTable({
    rows = [],
    setrowsConfirmedOrdersDespacth,
    onRowValueChange,
}) {
    const [editId, setEditId] = useState(null);
    const [draft, setDraft] = useState({});

    const columns = useMemo(
        () => [
            { key: "SIVariety", label: "Variety", align: "left", w: 120 },
            { key: "SIBF", label: "BF", align: "right", w: 70 },
            { key: "SIGSM", label: "GSM", align: "right", w: 70 },
            { key: "SIWidth", label: "Width", align: "right", w: 80 },
            { key: "SILength", label: "Length", align: "right", w: 90 },
            { key: "SIColor", label: "Color", align: "left", w: 90 },
            { key: "SIUnit", label: "Unit", align: "left", w: 70 },
            { key: "SIRequested_Qty", label: "Requested Qty", align: "right", w: 120 },
            { key: "SIUnitPrice", label: "Unit Price", align: "right", w: 110, editable: true },
            { key: "SIBillingRate", label: "Billing Rate", align: "right", w: 110 },
            { key: "SIDiscountType", label: "Discount type", align: "left", w: 120 },
            { key: "SIDiscount", label: "Discount / Unit", align: "right", w: 130 },
            { key: "SIConfirmed_Qty", label: "Confirmed Qty", align: "right", w: 130 },
            { key: "SIShipped_Qty", label: "Shipped Qty", align: "right", w: 120 },
            { key: "SIRemarks", label: "Remarks", align: "left", w: 160 },
            { key: "SIConfirmationNo", label: "Confirmation No", align: "left", w: 140 },
            { key: "SShipmentPlanNo", label: "Shipment Plan No", align: "left", w: 150 },
            { key: "SShipmentPlanDate", label: "Shipment Plan Date", align: "left", w: 150 },
            { key: "SIBeforeDiscountAmount", label: "Total Amt", align: "right", w: 120 },
            { key: "SIDiscountAmount", label: "Dis. Amt", align: "right", w: 120 },
            { key: "SIAfterDiscountAmount", label: "Ass. Amt", align: "right", w: 120 },
            { key: "SIFreightAmount", label: "Freight Amt", align: "right", w: 120 },
            { key: "SIInsuranceAmount", label: "Ins. Amt", align: "right", w: 120 },
            { key: "SIServiceChargeAmount", label: "S.C Amt", align: "right", w: 120 },
            { key: "SILineTaxableAmount", label: "Taxable Amt", align: "right", w: 130 },
            { key: "SICGSTAmount", label: "CGST Amt", align: "right", w: 110 },
            { key: "SISGSTAmount", label: "SGST Amt", align: "right", w: 110 },
            { key: "SIIGSTAmount", label: "IGST Amt", align: "right", w: 110 },
            { key: "SICessAmount", label: "Cess Amt", align: "right", w: 110 },
            { key: "SILineTaxAmount", label: "Tax Qty", align: "right", w: 100 },
        ],
        []
    );

    const handleEditClick = (id, row) => () => {
        setEditId(id);
        setDraft(row);
    };

    // const handleSaveClick = (id) => async () => {
    //     // persist draft to rows; call parent updater if needed
    //     setrowsConfirmedOrdersDespacth((prev) => prev.map((r) => (r.id === id ? { ...draft, isNew: false } : r)));
    //     setEditId(null);
    //     setDraft({});
    //     onRowValueChange();
    //     // CalculateSummaryForProducts();
    // };

    const handleSaveClick = (id) => async () => { 
    const updatedRows = rows.map(r =>
        r.id === id ? { ...draft, isNew: false } : r
    );

    setrowsConfirmedOrdersDespacth(updatedRows);
    setEditId(null);
    setDraft({});

    onRowValueChange(updatedRows); // pass updated rows
};


    const handleCancelClick = (id) => () => {
        setEditId(null);
        setDraft({});
        // if new row was being added and should be discarded
        const edited = rows.find((r) => r.id === id);
        if (edited?.isNew) {
            setrowsConfirmedOrdersDespacth((prev) => prev.filter((r) => r.id !== id));
        }
    };

    const renderCell = (row, col) => {
        if (editId === row.id && col.editable) {
            return (
                <TextField
                    size="small"
                    type="number"
                    value={draft[col.key] ?? ""}
                    onChange={(e) => setDraft((d) => ({ ...d, [col.key]: e.target.value }))}
                    inputProps={{ style: { textAlign: "right" } }}
                    sx={{ width: 110 }}
                />
            );
        }
        const v = row[col.key];
        return col.align === "right" ? <Num v={v} /> : (v ?? "");
    };

    return (
        <Card sx={{ bgcolor: "transparent", borderRadius: 1, mb: 1 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", color: "text.primary" }}>
                <Typography variant="h6" padding={1}>
                    Order Items <span style={{ fontSize: 12, color: "darkgray", marginLeft: 6 }}>(Unit price only editable)</span>
                </Typography>
            </Box>
            <Divider />
            <CardContent sx={{ p: 1 }}>
                <TableContainer
                    sx={{
                        maxHeight: 360, // vertical scroll area
                        overflowX: "auto",
                        border: 1,
                        borderColor: "divider",
                    }}
                >
                    <Table stickyHeader size="small" sx={{ minWidth: 1400 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ position: "sticky", left: 0, zIndex: 2, bgcolor: "background.paper" }}>Actions</TableCell>
                                {columns.map((c) => (
                                    <TableCell key={c.key} align={c.align} sx={{ minWidth: c.w }}>
                                        {c.label}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row, idx) => {
                                const isEdit = editId === row.id;
                                return (
                                    <TableRow
                                        key={row.id}
                                        hover
                                        sx={{
                                            "&:nth-of-type(odd)": { bgcolor: "action.hover" },
                                            height: 30,
                                        }}
                                    >
                                        {/* Sticky first column with actions */}
                                        <TableCell>
                                            {editId === row.id ? (
                                                <>
                                                    <IconButton size="small" onClick={handleSaveClick(row.id)}><SaveIcon fontSize="small" /></IconButton>
                                                    <IconButton size="small" onClick={handleCancelClick(row.id)}><CancelIcon fontSize="small" /></IconButton>
                                                </>
                                            ) : (
                                                <IconButton size="small" onClick={handleEditClick(row.id, row)}><EditIcon fontSize="small" /></IconButton>
                                            )}
                                        </TableCell>

                                        {columns.map((col) => (
                                            <TableCell key={col.key} align={col.align} sx={{ py: 0.25 }}>
                                                {renderCell(row, col)}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </CardContent>
        </Card>
    );
}
